import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { OrderedSet } from 'immutable';

import colorDataDefaults from './../ricos-schema/legacy/statics/color.defaults.json';
import type { ExtensionProps, MarkConfig, RicosExtension } from './../ricos-types';
import { Mark_Type } from './../ricos-types';
import { createRicosMarkExtension } from './../plugin-commons';

import { BACKGROUND_COLOR } from './data-hooks';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    colorBackground: {
      /**
       * Set a color mark
       */
      setHighlight: (color: string) => ReturnType;
      /**
       * Unset a color mark
       */
      unsetHighlight: () => ReturnType;
      /**
       * Apply highlight preview as decoration
       */
      previewHighlight: (color: string | null) => ReturnType;
    };
  }
}

const HIGHLIGHT_PREVIEW_PLUGIN_KEY = new PluginKey('highlightPreview');

export const tiptapColorBackgroundExtension = createRicosMarkExtension({
  type: 'mark',
  name: Mark_Type.COLOR_BACKGROUND,
  groups: [],
  reconfigure: (
    config: MarkConfig,
    _extensions: RicosExtension[],
    _props: ExtensionProps,
    settings: Record<string, unknown>
  ) => ({
    ...config,
    addOptions() {
      return {
        HTMLAttributes: {},
        ...settings,
      };
    },
  }),
  createExtensionConfig() {
    return {
      name: this.name,

      addAttributes() {
        return {
          color: { default: colorDataDefaults.background },
        };
      },

      parseHTML() {
        return [
          {
            style: 'background-color',
            getAttrs: (color: string) => ({ color }),
          },
        ];
      },

      renderHTML({ HTMLAttributes: { color } }) {
        const backgroundColor = this.options.styleSelectionPredicate?.(color)
          ? this.options.customStyleFn?.(OrderedSet([color]))?.backgroundColor
          : color;
        return [
          'span',
          {
            'data-hook': BACKGROUND_COLOR,
            style: `background-color: ${backgroundColor}; text-decoration: inherit;`,
          },
        ];
      },

      addCommands() {
        return {
          previewHighlight:
            color =>
            ({ tr, dispatch }) => {
              tr.setMeta(HIGHLIGHT_PREVIEW_PLUGIN_KEY, color);

              dispatch?.(tr);
              return true;
            },
          setHighlight:
            color =>
            ({ commands, tr, dispatch }) => {
              commands.setMark(this.name, { color });

              tr.setMeta(HIGHLIGHT_PREVIEW_PLUGIN_KEY, null);

              dispatch?.(tr);
              return true;
            },
          unsetHighlight:
            () =>
            ({ commands }) =>
              commands.setMark(this.name, { color: null }),
        };
      },

      addProseMirrorPlugins() {
        return [
          new Plugin({
            key: HIGHLIGHT_PREVIEW_PLUGIN_KEY,

            state: {
              init() {
                return { color: null };
              },
              apply(tr) {
                const newColor = tr.getMeta(HIGHLIGHT_PREVIEW_PLUGIN_KEY);
                return { color: newColor };
              },
            },

            props: {
              decorations(state) {
                const { color } = HIGHLIGHT_PREVIEW_PLUGIN_KEY.getState(state);
                const { from, to } = state.selection;

                if (!color || from === to) {
                  return null;
                }

                return DecorationSet.create(state.doc, [
                  Decoration.inline(from, to, {
                    style: `background-color: ${color}; text-decoration: inherit;`,
                  }),
                ]);
              },
            },
          }),
        ];
      },
    };
  },
});
